import { LoadingButton } from "@mui/lab";
import {
  Divider,
  FormControl,
  Grid,
  Radio,
  Table,
  TableCell,
  TableRow,
  TableBody,
  TableHead,
  TextField,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Collapse,
} from "@mui/material";
import RegularModal from "Components/UI/Modals/RegularModal";
import { useEffect, useRef, useState } from "react";
import { usePopupNotificationContext } from "Context/NotificationPopup";
import { useClients } from "Context/Clients";
import dayjs from "dayjs";
import { DatePicker } from "@mui/x-date-pickers";
import { MIN_SEARCH_LENGTH } from "utils/accountSearchFields";

const InvoiceControls = ({
  initialAmount,
  onClose,
  accountCharts,
  connectInvoicesSearch,
  searchResults,
  onSearch,
  searchLoading,
  connectLoading,
  connectInvoicesAccountChart,
}) => {
  const [searchValue, setSearchValue] = useState("");
  const [selectedSearchResultId, setSelectedSearchResultId] = useState(null);
  const [openSelect, setOpenSelect] = useState(false);
  const [openClientSelect, setOpenClientSelect] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState(null);
  const [showSelectClient, setShowSelectClient] = useState(false);
  const [selectedEndCustomerArticleId, setSelectedEndCustomerArticleId] =
    useState(null);
  const [connectAmount, setConnectAmount] = useState(initialAmount ?? "");
  const [showAccounts, setShowAccounts] = useState(false);
  const [bookingDate, setBookingDate] = useState(dayjs().format("YYYY-MM-DD"));

  const { clients, fetchClients } = useClients();
  const { openError, openSuccess } = usePopupNotificationContext();

  const selectRef = useRef(null);
  const menuRef = useRef(null);
  const selectClientRef = useRef(null);
  const menuClientRef = useRef(null);

  const handleConnect = async () => {
    if (showAccounts) {
      if (showSelectClient && !selectedClientId) {
        openError("Ingen klient vald", 3000);
        return;
      }
      await connectInvoicesAccountChart(
        selectedEndCustomerArticleId,
        selectedClientId,
        Number(connectAmount),
        bookingDate
      );
      return;
    } else {
      if (!selectedSearchResultId) {
        openError("Ingen faktura vald", 3000);
        return;
      }
      await connectInvoicesSearch(
        selectedSearchResultId,
        Number(connectAmount)
      );
      return;
    }
  };

  useEffect(() => {
    setSelectedClientId(null);
    setSelectedSearchResultId(null);
    setSelectedEndCustomerArticleId(null);
  }, [showAccounts]);

  // Workaround for Select + ClickAwayListener so ClickAwayListener is not triggered
  useEffect(() => {
    const onMouseUp = (e) => {
      if (e.target.parentElement === selectRef.current) {
        setOpenSelect(true);
      } else {
        if (e.target.parentElement !== menuRef.current) {
          setOpenSelect(false);
        }
      }

      if (e.target.parentElement === selectClientRef.current) {
        setOpenClientSelect(true);
      } else {
        if (e.target.parentElement !== menuClientRef.current) {
          setOpenClientSelect(false);
        }
      }
    };
    window.addEventListener("mouseup", onMouseUp);
    return () => {
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, []);

  useEffect(() => {
    const selectedChart = accountCharts.find(
      (chart) => chart.Id === selectedEndCustomerArticleId
    );
    setSelectedClientId(null);
    if (selectedChart) {
      setShowSelectClient(selectedChart.AccountNumber === "1340-D");
    } else {
      setShowSelectClient(false);
    }
  }, [selectedEndCustomerArticleId, accountCharts]);

  useEffect(() => {
    fetchClients();
  }, []);

  useEffect(() => {
    setConnectAmount(initialAmount);
  }, [initialAmount]);

  return (
    <RegularModal open={true} onClose={onClose}>
      <Grid item container spacing={2}>
        <Grid item container spacing={2}>
          <Grid item md={5}>
            <FormControl fullWidth>
              <TextField
                fullWidth
                type="number"
                label={"Koppla oplacerat belopp"}
                value={connectAmount}
                onChange={(e) => setConnectAmount(e.target.value)}
                size="small"
              />
            </FormControl>
          </Grid>
          <Grid item md={2}>
            <LoadingButton
              sx={{ width: "100%" }}
              variant="contained"
              onClick={handleConnect}
              loading={connectLoading}
              disabled={
                (showAccounts && !selectedEndCustomerArticleId) ||
                (!showAccounts && !selectedSearchResultId)
              }
            >
              Koppla
            </LoadingButton>
          </Grid>
        </Grid>
        <Grid item container>
          <Grid item>
            <FormControlLabel
              control={
                <Switch
                  checked={showAccounts}
                  onChange={() => setShowAccounts(!showAccounts)}
                />
              }
              label="Koppla mot konto"
            />
          </Grid>
        </Grid>
        <Grid item md={6}>
          <Collapse
            in={showAccounts}
            sx={{
              "& .MuiCollapse-wrapperInner": {
                display: "flex",
                flexDirection: "column",
                gap: "1rem",
              },
            }}
          >
            <DatePicker
              format="YYYY-MM-DD"
              label="Bokföringsdatum"
              value={dayjs(bookingDate)}
              onChange={(val) =>
                setBookingDate(dayjs(val).format("YYYY-MM-DD"))
              }
              slotProps={{ textField: { size: "small" } }}
            />
            <FormControl fullWidth>
              <InputLabel id="input-label">Välj konto</InputLabel>
              <Select
                ref={selectRef} // Workaround for select + ClickAwayListener so ClickAwayListener is not triggered
                MenuProps={{
                  MenuListProps: {
                    ref: menuRef, // Workaround for select + ClickAwayListener so ClickAwayListener is not triggered
                  },
                }}
                labelId="input-label"
                label="Välj konto"
                open={openSelect}
                size="small"
                onClose={() => setOpenSelect(false)}
                value={selectedEndCustomerArticleId}
                onChange={(e) => {
                  setSelectedEndCustomerArticleId(e.target.value);
                }}
              >
                <MenuItem key={null} value={null}>
                  {"Inget"}
                </MenuItem>
                {accountCharts.map((account) => (
                  <MenuItem key={account.Id} value={account.Id}>
                    {account.Title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {showSelectClient ? (
              <FormControl fullWidth>
                <InputLabel id="input-label">Välj klient</InputLabel>
                <Select
                  ref={selectClientRef} // Workaround for select + ClickAwayListener so ClickAwayListener is not triggered
                  MenuProps={{
                    MenuListProps: {
                      ref: menuClientRef, // Workaround for select + ClickAwayListener so ClickAwayListener is not triggered
                    },
                  }}
                  size="small"
                  labelId="input-label"
                  label="Välj klient"
                  open={openClientSelect}
                  onClose={() => setOpenClientSelect(false)}
                  value={selectedClientId}
                  onChange={(e) => {
                    setSelectedClientId(e.target.value);
                  }}
                >
                  <MenuItem key={null} value={null}>
                    {"Inget"}
                  </MenuItem>
                  {clients
                    .sort((c1, c2) => c1.Title.localeCompare(c2.Title))
                    .map((client) => (
                      <MenuItem key={client.Id} value={client.Id}>
                        {client.Title}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            ) : null}
          </Collapse>
        </Grid>
        <Grid item md={12}>
          <Collapse in={!showAccounts}>
            <Grid item container spacing={2}>
              <Grid item container spacing={2}>
                <Grid item md={4}>
                  <TextField
                    fullWidth
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                    size="small"
                    helperText={
                      searchValue && searchValue.trim().length < MIN_SEARCH_LENGTH
                        ? `Skriv minst ${MIN_SEARCH_LENGTH} tecken`
                        : " "
                    }
                  />
                </Grid>
                <Grid item md={1}>
                  <LoadingButton
                    fullWidth
                    variant="contained"
                    disabled={
                      !searchValue ||
                      searchValue.trim().length < MIN_SEARCH_LENGTH
                    }
                    loading={searchLoading}
                    onClick={() => onSearch(searchValue.trim())}
                  >
                    Sök
                  </LoadingButton>
                </Grid>
              </Grid>
              <Grid item container md={12}>
                <div
                  style={{
                    maxHeight: "400px",
                    overflow: "auto",
                    width: "100%",
                  }}
                >
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell></TableCell>
                        <TableCell>{"Fakturanummer"}</TableCell>
                        <TableCell>{"Kundnummer"}</TableCell>
                        <TableCell>{"Fakturadatum"}</TableCell>
                        <TableCell>{"Belopp"}</TableCell>
                        <TableCell>{"Återstående belopp"}</TableCell>
                        <TableCell>{"Klientnamn"}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {searchResults.map((result, index) => {
                        return (
                          <TableRow key={index}>
                            <TableCell>
                              <Radio
                                disabled={result.RemainingAmount === 0}
                                checked={
                                  selectedSearchResultId === result.InvoiceID
                                }
                                onChange={() =>
                                  setSelectedSearchResultId(result.InvoiceID)
                                }
                                value={result.InvoiceID}
                              />
                            </TableCell>
                            <TableCell>{result.InvoiceNumber}</TableCell>
                            <TableCell>{result.CustomerNumber}</TableCell>
                            <TableCell>
                              {dayjs(result.InvoiceDate).format("YYYY-MM-DD")}
                            </TableCell>
                            <TableCell>{result.Price}</TableCell>
                            <TableCell>{result.RemainingAmount}</TableCell>
                            <TableCell>{result.ClientName}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </Grid>
            </Grid>
          </Collapse>
        </Grid>
      </Grid>
    </RegularModal>
  );
};

export default InvoiceControls;
